import { Button, Card, Drawer, Grid, Heading, List, StepList } from '@boltenergy-be/design-system'
import styles from './HowItWorks.module.scss'
import ActionCard from 'pages/app/referral/components/action-card/ActionCard'
import { Trans, useTranslation } from 'react-i18next'
import LinkAndShareCard from 'pages/app/referral/components/link-and-share-card/LinkAndShareCard'
import { useGetReferralCodeQuery } from 'store/queries/bolt-api/customers'
import { getBoltWebsiteOrigin } from 'utils/origins'
import { useStoreSelector } from 'hooks/store'
import { useGetLanguage } from 'hooks/use-get-language'
import { REFERRAL_AMOUNT_GIVE, REFERRAL_AMOUNT_RECEIVE } from 'constants/referral'
import { useGetActiveReferralActionQuery } from 'store/queries/cms-api'
import { useState } from 'react'
import parse from 'html-react-parser'
import { LANGUAGES } from '@boltenergy-be/shared'

const HowItWorks = () => {
  // Redux
  const { contact } = useStoreSelector((store) => store.contact)

  // i18n
  const { t } = useTranslation('referral')
  const language = useGetLanguage()

  // Redux queries
  const { data: activeAction } = useGetActiveReferralActionQuery({ language }, { skip: true }) // Skipped because there is no active referral action
  const { data: referralCodeData, isError: isReferralCodeError } = useGetReferralCodeQuery(
    { email: contact?.email || '' },
    { skip: !contact }
  )

  // Local state
  const [showReferralTerms, setShowReferralTerms] = useState<boolean>(false)

  // Local constants
  const amountGive = activeAction?.reduction || REFERRAL_AMOUNT_GIVE
  const amountReceive = activeAction?.reduction || REFERRAL_AMOUNT_RECEIVE
  const referralLink = `${getBoltWebsiteOrigin()}${
    language === LANGUAGES.FR ? 'fr/convaincre-un-ami' : 'nl/overtuig-een-vriend'
  }?referralcode=${referralCodeData?.code ?? ''}`

  return (
    <section className={styles['how-it-works']}>
      <article className={styles.details}>
        {/* HOW IT WORKS */}
        <section className={styles['steps-card']}>
          <Heading as="h2" variant="h4" serif>
            {t(($) => $.howItWorks.heading)}
          </Heading>

          <StepList size="small" className={styles['steps-list']}>
            {[1, 2, 3].map((bullet) => (
              <li key={bullet}>
                <strong>
                  {t(($) => $.howItWorks.steps[bullet as 1 | 2 | 3].heading, {
                    amountGive: amountGive,
                    amountReceive: amountReceive
                  })}
                </strong>
                <small>
                  <Trans
                    t={t}
                    i18nKey={($) => $.howItWorks.steps[bullet as 1 | 2 | 3].paragraph}
                    values={{ amountGive, amountReceive }}
                    components={{
                      CustomButton: <Button representation="link" variant="inline" onClick={() => setShowReferralTerms(true)} />
                    }}
                  />
                </small>
              </li>
            ))}
          </StepList>
        </section>

        {/* SHARE */}
        <LinkAndShareCard
          amount={amountGive}
          shareLink={referralLink}
          hasError={isReferralCodeError}
          activeAction={activeAction || undefined}
        />
      </article>

      {/* ACTION */}
      {!!activeAction && <ActionCard {...{ activeAction }} />}

      <Grid
        title={t(($) => $.merch.title)}
        description={t(($) => $.merch.description)}
        fluid
        serifTitle
        titleVariant="h4"
        classnames={{
          container: styles.merch,
          list: styles.grid
        }}
      >
        {t(($) => $.merch.rewards, {
          returnObjects: true
        }).map((reward, index) => (
          <Card key={`reward-${index}`} className={styles['merch-card']} color="warm">
            <Heading as="h2" variant="h4" serif>
              {reward.title}
            </Heading>
            <p>{parse(reward.description)}</p>
          </Card>
        ))}
      </Grid>

      <Drawer isOpen={showReferralTerms} onRequestClose={() => setShowReferralTerms(false)} title={t(($) => $.howItWorks.drawer.title)}>
        <p>
          {t(($) => $.howItWorks.drawer.description, {
            amountGive: amountGive,
            amountReceive: amountReceive
          })}
        </p>
        <List as="ul">
          {t(($) => $.howItWorks.drawer.bullets, {
            returnObjects: true
          }).map((bullet, index) => (
            <li key={`bullet-${index}`}>{bullet}</li>
          ))}
        </List>
      </Drawer>
    </section>
  )
}

export default HowItWorks
